/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, FileText, Cookie, Scale, FileCode, Copyright } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Logo from '@/components/Logo';
import { LEGAL_OWNER } from '@/lib/legalOwner';

const LEGAL_TABS = [
  { key: 'privacy', to: '/legal/privacy', label: 'Privacy Policy', Icon: ShieldCheck },
  { key: 'terms', to: '/legal/terms', label: 'Terms of Service', Icon: FileCode },
  { key: 'cookie', to: '/legal/cookie', label: 'Cookie Policy', Icon: Cookie },
  { key: 'dpa', to: '/legal/dpa', label: 'DPA', Icon: Scale },
  { key: 'license', to: '/legal/license', label: 'Licenza & IP', Icon: Copyright },
];

/**
 * Reusable wrapper for legal pages (Privacy, Cookie, DPA).
 * - Public accessible (no auth needed).
 * - Includes back button, header, and tab-like navigation between legal docs.
 */
const LegalPageLayout = ({ activeKey, title, subtitle, lastUpdated, children }) => {
  const navigate = useNavigate();

  // Scroll top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeKey]);

  return (
    <div className="min-h-screen bg-background" data-testid={`legal-page-${activeKey}`}>
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-background/85 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-1"
            data-testid="legal-back-button"
          >
            <ArrowLeft className="h-4 w-4" /> Indietro
          </Button>
          <Link to="/welcome" className="flex items-center gap-2" data-testid="legal-home-link">
            <Logo size={24} withText={false} />
            <span className="font-display font-semibold text-sm hidden sm:inline">SanitàNexus</span>
          </Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-semibold uppercase tracking-wider mb-3">
            <FileText className="h-3 w-3" /> Documento Legale
          </div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight" data-testid="legal-page-title">
            {title}
          </h1>
          {subtitle && (
            <p className="text-muted-foreground mt-2 text-sm md:text-base">{subtitle}</p>
          )}
          {lastUpdated && (
            <p className="text-xs text-muted-foreground mt-3">
              <span className="font-medium">Ultimo aggiornamento:</span> {lastUpdated}
            </p>
          )}
        </div>

        {/* Tab navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1 -mx-1 px-1">
          {LEGAL_TABS.map(({ key, to, label, Icon }) => {
            const active = key === activeKey;
            return (
              <Link
                key={key}
                to={to}
                data-testid={`legal-tab-${key}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-secondary/60 text-foreground hover:bg-secondary'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </Link>
            );
          })}
        </div>

        {/* Content */}
        <article
          className="prose prose-sm md:prose-base max-w-none dark:prose-invert
            prose-headings:font-display prose-headings:tracking-tight prose-headings:font-semibold
            prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-border
            prose-h3:text-base prose-h3:mt-6 prose-h3:mb-2
            prose-p:leading-relaxed prose-p:text-foreground/90
            prose-li:text-foreground/90 prose-li:my-1
            prose-strong:text-foreground prose-strong:font-semibold
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-table:text-sm"
          data-testid="legal-content"
        >
          {children}
        </article>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-border space-y-3">
          <div className="rounded-lg bg-muted/40 p-4 text-xs" data-testid="legal-owner-block">
            <div className="font-semibold text-foreground mb-1.5">Titolare del Trattamento</div>
            <div className="space-y-0.5 text-muted-foreground">
              <div>{LEGAL_OWNER.fullName} — Codice Fiscale: <span className="font-mono">{LEGAL_OWNER.fiscalCode}</span></div>
              <div>{LEGAL_OWNER.addressLine}</div>
              <div>
                <a href={`mailto:${LEGAL_OWNER.contactEmail}`} className="text-primary hover:underline">
                  {LEGAL_OWNER.contactEmail}
                </a>
              </div>
              <div className="italic mt-1">{LEGAL_OWNER.vatExemptionNotice}</div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            Per richieste relative ai tuoi dati personali, contatta il Titolare all'indirizzo email indicato sopra.
          </p>
          <div className="flex items-center justify-center gap-3 text-xs flex-wrap">
            {LEGAL_TABS.filter((t) => t.key !== activeKey).map(({ key, to, label }) => (
              <Link
                key={key}
                to={to}
                className="text-primary hover:underline"
                data-testid={`legal-footer-link-${key}`}
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPageLayout;
